import { useEffect, useState } from 'react';
import type { TrimRect } from '@/shared/lib/image-trim';
import { scanContentBounds } from './default-crop';
import { createLogger } from '@/shared/lib/logger';

const logger = createLogger('useContentBounds');

interface UseContentBoundsProps {
  imageUrl: string;
  /** Wait for the loader: natural size is only known once the image decoded. */
  enabled: boolean;
  naturalSize: { width: number; height: number };
}

interface UseContentBoundsReturn {
  /** Content rect in natural pixels, or null when there is no border to drop. */
  bounds: TrimRect | null;
  /** False until the scan finished — the caller must not frame before this. */
  ready: boolean;
}

/**
 * Finds the photo inside a letterboxed screenshot, so the cropper can open on
 * the photo rather than on the black bars. See `image-trim.ts` for the why.
 */
export function useContentBounds({
  imageUrl,
  enabled,
  naturalSize,
}: UseContentBoundsProps): UseContentBoundsReturn {
  const [bounds, setBounds] = useState<TrimRect | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    setBounds(null);
    setReady(false);

    if (!enabled || !imageUrl || !naturalSize.width || !naturalSize.height) {
      return;
    }

    let cancelled = false;

    const scan = async () => {
      try {
        const image = new Image();
        image.decoding = 'async';
        image.src = imageUrl;
        await image.decode();

        if (cancelled) return;

        const found = scanContentBounds(image, naturalSize);

        if (cancelled) return;

        setBounds(found);
        setReady(true);
      } catch (error) {
        // A failed scan must never block the cropper: fall through to the
        // untrimmed frame, which is exactly the behaviour we had before.
        logger.warn('Content-bounds scan failed, framing the full image', error);
        if (!cancelled) {
          setBounds(null);
          setReady(true);
        }
      }
    };

    void scan();

    return () => {
      cancelled = true;
    };
  }, [imageUrl, enabled, naturalSize.width, naturalSize.height]);

  return { bounds, ready };
}
